import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { noop } from 'ui-components/default-props/noop';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { PICKING_METHODS, Steps } from './constants';
import { useSetupOrdersState, useShipFromState } from './setup-orders-hooks';
import { useVerifyInventoryState } from './verify-inventory-hooks';
import { useVerifyOrdersState } from './verify-orders-hooks';
import { useInventoryTrackingEnabled } from '../../../../hooks/use-seller-setting';
import { addedFulfillmentPlansToCreatedBatchSuccess } from '../../../../store/batches/batches.actions';
import { createNewBatchAsync, saveBatchAsync } from '../../../../store/batches/batches.api';
import { getBatchDetails, removeFulfillmentPlansFromBatch } from '../../../../store/batches/details/batch.details.actions';
import { forceAddFulfillmentPlansPromise } from '../../../../store/batches/details/batch.details.api';
import { bulkGetBulkJobData, updateBulkShipping } from '../../../../store/bulk/update/bulk.update.api';
import { bulkloadFulfillmentPlans } from '../../../../store/bulkload/bulkload.actions';
import Global from '../../../../store/global-selectors';
import { setSelectedOrdersPreCheck } from '../../../../store/orders/selected-orders/orders.selected-orders.actions';
import { messages } from '../messages';
import { addOrdersToQueue, createPickBatchFromLabelBatch, removeOrdersFromBatches } from '../pick-batch.api';

/**
 * @typedef {import('./verify-orders-hooks').OrderValidationErrors} OrderValidationErrors
 * @typedef {import('./verify-inventory-hooks').InventoryValidationError} InventoryValidationError
 * @typedef {import('../../../../prop-types/ship-from').ShipFromPropType} ShipFromPropType
 */

/**
 * Context value containing all the necessary state and functions for the Ready to Pick modal.
 * @typedef {object} ReadyToPickContextValue
 * @property {boolean} areAllProblemOrdersSelected
 * @property {boolean} areAnyProblemOrdersSelected
 * @property {string} batchName
 * @property {boolean} canCompleteModal
 * @property {boolean} canSetupOrdersContinue
 * @property {boolean} canVerifyInventoryContinue
 * @property {boolean} canVerifyOrdersContinue
 * @property {number} currentStep
 * @property {boolean} doesShipFromHaveInventory
 * @property {boolean} doesShipFromHaveNativeInventory
 * @property {boolean} isBatch
 * @property {boolean} isLoading
 * @property {() => Promise<void>} onModalCompletion
 * @property {() => Promise<void>} onSetupOrdersContinue
 * @property {() => Promise<void>} onVerifyInventoryContinue
 * @property {() => Promise<void>} onVerifyOrdersContinue
 * @property {string} pickingMethod
 * @property {() => Promise<void>} prepareVerifyInventoryStep
 * @property {Array<OrderValidationErrors | InventoryValidationError>} problemOrders
 * @property {(orderIds: Array<string>) => void} removeProblemOrder
 * @property {() => void} removeSelectedProblemOrder
 * @property {Array<string>} selectedProblemOrderIds
 * @property {Array<ShipFromPropType>} sellerShipFroms
 * @property {(string) => void} setBatchName
 * @property {(number) => void} setCurrentStep
 * @property {(orderIds: Array<string>) => void} setSelectedProblemOrderIds
 * @property {(string) => void} setPickingMethod
 * @property {(string) => void} setShipFromId
 * @property {string} shipFromId
 * @property {(boolean) => void} toggleAllOrdersSelections
 * @property {(event: React.ChangeEvent<HTMLInputElement>, order: { id: string }) => void} toggleOrderSelection
 */

export const ReadyToPickContext = createContext({});
/**
 * @returns {ReadyToPickContextValue}
 */
export const useReadyToPickContext = () => useContext(ReadyToPickContext);

const bulkUpdateShipFrom = async (currentFulfillmentPlanIds, shipFromId) => {
  try {
    const {
      data: { bulkUpdateJobGuid },
    } = await bulkGetBulkJobData({
      fulfillmentPlanIds: currentFulfillmentPlanIds,
    });
    const request = {
      bulkUpdateJobGuid,
      shippingConfiguration: { shipFromId },
    };
    const { data } = await updateBulkShipping(request);
    const { jobGuids } = data ?? {};
    const [jobGuid] = jobGuids ?? [];
    return jobGuid;
  } catch {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.handleBulkUpdateSaveValidateFailureTitle} />,
      content: <FormattedMessage {...messages.handleBulkUpdateSaveValidateFailureMessage} />,
    });
  }
};

/**
 * @param {object} props
 * @param {string} [props.batchName]
 * @param {React.ReactNode} props.children
 * @param {Array<string>} props.fulfillmentPlanIds
 * @param {string} [props.fulfillmentPlanSetId]
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
export const ReadyToPickContextProvider = ({
  batchName: initialBatchName,
  children,
  fulfillmentPlanIds,
  fulfillmentPlanSetId: fulfillmentPlanSetIdProp,
  onClose,
}) => {
  const dispatch = useDispatch();

  const inventoryTrackingIsEnabled = useInventoryTrackingEnabled();
  const userId = useSelector(Global.selectUserCurrentUserId);

  // Shared state
  const isBatch = !!fulfillmentPlanSetIdProp;
  const [currentStep, setCurrentStep] = useState(Steps.verifyInformation);
  const [currentFulfillmentPlanIds, setCurrentFulfillmentPlanIds] = useState(fulfillmentPlanIds);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);
  const { shipFromId, sellerShipFroms, setShipFromId } = useShipFromState();

  // Verify Orders & Verify Inventory card state
  const [problemOrders, setProblemOrders] = useState([]);
  const [selectedProblemOrderIds, setSelectedProblemOrderIds] = useState([]);
  const areAnyProblemOrdersSelected = setSelectedProblemOrderIds.length > 0;
  const areAllProblemOrdersSelected = setSelectedProblemOrderIds.length === problemOrders.length;

  // During step transitions, both completing the current step and preparing the next step are done. The step
  // preparations generally consist of making API calls to gather the validation errors to display in that step. If
  // there are no errors, the next step completion and (potential) step preparation are performed. This make one step's
  // continue callback dependent on the next steps continue callback, and is why these are in reverse order.
  const { canVerifyInventoryContinue, onVerifyInventoryContinue, prepareVerifyInventoryStep } = useVerifyInventoryState({
    currentFulfillmentPlanIds,
    inventoryTrackingIsEnabled,
    setIsLoading,
    problemOrders,
    setCurrentStep,
    setProblemOrders,
    setSelectedProblemOrderIds,
    shipFromId,
  });
  const { canVerifyOrdersContinue, onVerifyOrdersContinue, prepareVerifyOrdersStep } = useVerifyOrdersState({
    bulkUpdateShipFrom,
    currentFulfillmentPlanIds,
    inventoryTrackingIsEnabled,
    isBatch,
    prepareVerifyInventoryStep,
    problemOrders,
    setCurrentStep,
    setIsLoading,
    setProblemOrders,
    shipFromId,
  });
  const {
    batchName,
    canSetupOrdersContinue,
    doesShipFromHaveInventory,
    doesShipFromHaveNativeInventory,
    pickingMethod,
    onSetupOrdersContinue,
    setBatchName,
    setPickingMethod,
  } = useSetupOrdersState({
    isBatch,
    initialBatchName,
    inventoryTrackingIsEnabled,
    prepareVerifyOrdersStep,
    setCurrentStep,
    shipFromId,
  });
  const canCompleteModal = currentStep === Steps.complete;

  // Selected Orders Logic
  const toggleOrderSelection = useCallback((__, { id: idToToggle }) => {
    setSelectedProblemOrderIds((prevSelectedProblemOrderIds) => {
      const isSelected = prevSelectedProblemOrderIds.some((id) => id === idToToggle);
      if (isSelected) {
        return prevSelectedProblemOrderIds.filter((id) => id !== idToToggle);
      }
      return [...prevSelectedProblemOrderIds, idToToggle];
    });
  }, []);
  const toggleAllOrdersSelections = useCallback(
    (isChecked) => {
      if (!isChecked) {
        setSelectedProblemOrderIds([]);
        return;
      }
      const allIds = problemOrders.map((order) => order.fulfillmentPlanId);
      setSelectedProblemOrderIds(allIds);
    },
    [problemOrders],
  );

  // Removing orders callbacks
  const handleRemoveOrders = useCallback(
    (removedOrderIds) => {
      const newFulfillmentPlanIds = currentFulfillmentPlanIds.filter((id) => !removedOrderIds.includes(id));
      setCurrentFulfillmentPlanIds(newFulfillmentPlanIds);
      if (isBatch) {
        dispatch(
          removeFulfillmentPlansFromBatch({
            fulfillmentPlanSetId: fulfillmentPlanSetIdProp,
            fulfillmentPlanIds: removedOrderIds,
          }),
        );
      } else {
        dispatch(setSelectedOrdersPreCheck(newFulfillmentPlanIds));
      }
    },
    [currentFulfillmentPlanIds, dispatch, fulfillmentPlanSetIdProp, isBatch],
  );
  const removeProblemOrder = useCallback(
    ([removedOrderId]) => {
      setProblemOrders((prevProblemOrders) => prevProblemOrders.filter((order) => order.fulfillmentPlanId !== removedOrderId));
      setSelectedProblemOrderIds((prevSelectedProblemOrderIds) => prevSelectedProblemOrderIds.filter((id) => id !== removedOrderId));
      handleRemoveOrders([removedOrderId]);
    },
    [handleRemoveOrders],
  );
  const removeSelectedProblemOrder = useCallback(() => {
    setProblemOrders((prevProblemOrders) =>
      prevProblemOrders.filter((order) => !selectedProblemOrderIds.includes(order.fulfillmentPlanId)),
    );
    handleRemoveOrders(selectedProblemOrderIds);
    setSelectedProblemOrderIds([]);
  }, [handleRemoveOrders, selectedProblemOrderIds]);

  const onModalCompletion = useCallback(async () => {
    setIsLoadingConfirm(true);

    // Returns the resolved batch ID and name after creating or saving the batch.
    const resolveBatch = async () => {
      if (isBatch) {
        await saveBatchAsync(fulfillmentPlanSetIdProp, batchName, '', userId);
        return { fulfillmentPlanSetId: fulfillmentPlanSetIdProp, resolvedBatchName: batchName };
      }
      // "Ready to Pick" was clicked from the awaiting shipment grid with picking method Batch
      const { data } = await createNewBatchAsync(batchName, '', userId);
      const { fulfillmentPlanSetId: newBatchId, fulfillmentPlanSetName: newBatchName } = data ?? {};
      await forceAddFulfillmentPlansPromise(newBatchId, currentFulfillmentPlanIds);
      return { fulfillmentPlanSetId: newBatchId, resolvedBatchName: newBatchName };
    };

    try {
      if (!inventoryTrackingIsEnabled) {
        await bulkUpdateShipFrom(currentFulfillmentPlanIds, shipFromId);
      }

      if (pickingMethod === PICKING_METHODS.QUEUE) {
        const selectedShipFrom = sellerShipFroms.find((sf) => sf.id === shipFromId);
        await removeOrdersFromBatches(currentFulfillmentPlanIds).catch(noop);
        await addOrdersToQueue(currentFulfillmentPlanIds, selectedShipFrom?.inventorySourceId);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.createPickBatchFromLabelBatchSuccessTitle} />,
          content: (
            <FormattedMessage {...messages.addOrdersToPickingQueueSuccessMessage} values={{ count: currentFulfillmentPlanIds.length }} />
          ),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        return;
      }

      const { fulfillmentPlanSetId, resolvedBatchName } = await resolveBatch();
      await createPickBatchFromLabelBatch(fulfillmentPlanSetId);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.createPickBatchFromLabelBatchSuccessTitle} />,
        content: (
          <FormattedMessage
            {...messages.createPickBatchFromLabelBatchSuccessMessage}
            values={{ count: currentFulfillmentPlanIds.length, batchName: resolvedBatchName }}
          />
        ),
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
      dispatch(addedFulfillmentPlansToCreatedBatchSuccess(fulfillmentPlanSetId));
      dispatch(getBatchDetails(fulfillmentPlanSetId));
      setIsLoadingConfirm(false);
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.createPickBatchFromLabelBatchFailureTitle} />,
        content: <FormattedMessage {...messages.createPickBatchFromLabelBatchFailureMessage} />,
      });
    } finally {
      dispatch(bulkloadFulfillmentPlans(fulfillmentPlanIds));
      onClose();
    }
  }, [
    batchName,
    currentFulfillmentPlanIds,
    dispatch,
    fulfillmentPlanIds,
    fulfillmentPlanSetIdProp,
    inventoryTrackingIsEnabled,
    isBatch,
    onClose,
    pickingMethod,
    sellerShipFroms,
    shipFromId,
    userId,
  ]);

  const value = useMemo(
    () => ({
      areAllProblemOrdersSelected,
      areAnyProblemOrdersSelected,
      batchName,
      canCompleteModal,
      canSetupOrdersContinue,
      canVerifyInventoryContinue,
      canVerifyOrdersContinue,
      currentStep,
      doesShipFromHaveInventory,
      doesShipFromHaveNativeInventory,
      isBatch,
      isLoading,
      onModalCompletion,
      onSetupOrdersContinue,
      onVerifyInventoryContinue,
      onVerifyOrdersContinue,
      pickingMethod,
      prepareVerifyInventoryStep,
      problemOrders,
      removeProblemOrder,
      removeSelectedProblemOrder,
      selectedProblemOrderIds,
      sellerShipFroms,
      setBatchName,
      setCurrentStep,
      setSelectedProblemOrderIds,
      setPickingMethod,
      setShipFromId,
      shipFromId,
      toggleAllOrdersSelections,
      toggleOrderSelection,
      isLoadingConfirm,
    }),
    [
      areAllProblemOrdersSelected,
      areAnyProblemOrdersSelected,
      batchName,
      canCompleteModal,
      canSetupOrdersContinue,
      canVerifyInventoryContinue,
      canVerifyOrdersContinue,
      currentStep,
      doesShipFromHaveInventory,
      doesShipFromHaveNativeInventory,
      isBatch,
      isLoading,
      onModalCompletion,
      onSetupOrdersContinue,
      onVerifyInventoryContinue,
      onVerifyOrdersContinue,
      pickingMethod,
      prepareVerifyInventoryStep,
      problemOrders,
      removeProblemOrder,
      removeSelectedProblemOrder,
      selectedProblemOrderIds,
      sellerShipFroms,
      setBatchName,
      setPickingMethod,
      setShipFromId,
      shipFromId,
      toggleAllOrdersSelections,
      toggleOrderSelection,
      isLoadingConfirm,
    ],
  );

  return <ReadyToPickContext.Provider value={value}>{children}</ReadyToPickContext.Provider>;
};

/**
 * Custom hook to get step information for a given card step.
 * @param {number} cardStep - The step number for the requesting card component.
 * @returns {{isLoading: boolean, isActiveStep: boolean, isStepComplete: boolean}}
 */
export const useStepInfo = (cardStep) => {
  const { currentStep, isLoading: isLoadingProp } = useReadyToPickContext();
  return useMemo(() => {
    const isActiveStep = currentStep === cardStep;
    const isStepComplete = currentStep > cardStep;
    const isLoading = isLoadingProp && isActiveStep;

    return { isActiveStep, isLoading, isStepComplete };
  }, [cardStep, currentStep, isLoadingProp]);
};
